import type { BizType } from "./biz-type";
import { parseBizTypeFromQuery } from "./biz-type";
import type { Rv2Lang } from "./i18n";
import { withLang } from "./i18n";
import { rv2T } from "./i18n/t";

export type { BizType } from "./biz-type";

export type PvTier = "1" | "2-3" | "4-6" | "7-10" | "11+";
export type BillPeriod = "mensuel" | "annuel";

const BILL_PERIOD_VALUES: BillPeriod[] = ["mensuel", "annuel"];

const PRICES: Record<PvTier, Record<BillPeriod, number | null>> = {
  "1": { mensuel: 300, annuel: 3000 },
  "2-3": { mensuel: 600, annuel: 6000 },
  "4-6": { mensuel: 900, annuel: 9000 },
  "7-10": { mensuel: 1200, annuel: 12000 },
  "11+": { mensuel: null, annuel: null },
};

export type BillingDisplay = {
  price: number | null;
  priceLabel: string;
  unit: string;
  note: string;
  save: string;
  hasSave: boolean;
  isQuote: boolean;
};

export function getPrice(pv: PvTier, period: BillPeriod): number | null {
  return PRICES[pv]?.[period] ?? null;
}

export function getOnlineAddonPrice(period: BillPeriod): number | null {
  return getPrice("1", period);
}

type CatalogPrices = Record<BillPeriod, number | null>;

export function resolveLandingCatalogPrices(pv: PvTier, biz: BizType): CatalogPrices {
  const base = PRICES[pv];
  const addon = PRICES["1"];

  if (biz === "online") {
    return { ...addon };
  }

  if (biz === "mixte") {
    return {
      mensuel: base.mensuel == null ? null : base.mensuel + addon.mensuel!,
      annuel: base.annuel == null ? null : base.annuel + addon.annuel!,
    };
  }

  return { ...base };
}

export type CommercialOfferLike = {
  applied?: boolean;
  annualMad?: number | null;
  circuit?: string;
  biz?: string | null;
};

/** True when sales set an annual price that differs from the public catalog (or closed circuit). */
export function hasModifiedCommercialOffer(
  offer: CommercialOfferLike | undefined,
  offerPvTier: PvTier | null,
  biz: BizType = "physique",
): boolean {
  if (!offer?.applied || offer.annualMad == null || !offerPvTier) return false;
  if (offer.circuit === "ferme") return true;
  const offerBiz = parseBizTypeFromQuery(offer.biz ?? null) ?? biz;
  const catalogAnnual = resolveLandingCatalogPrices(offerPvTier, offerBiz).annuel;
  if (catalogAnnual == null) return true;
  return offer.annualMad !== catalogAnnual;
}

/** Map free PDV count → catalog tier (mirrors activation / API). */
export function pvCountToTier(pvCount: number | string | null | undefined): PvTier | null {
  const n = Number(pvCount);
  if (!Number.isFinite(n) || n < 1) return null;
  if (n <= 1) return "1";
  if (n <= 3) return "2-3";
  if (n <= 6) return "4-6";
  if (n <= 10) return "7-10";
  return "11+";
}

export function formatMad(n: number): string {
  return n.toLocaleString("fr-FR");
}

const PV_TIER_KEYS: Record<PvTier, string> = {
  "1": "commercants.form.pv1",
  "2-3": "commercants.form.pv23",
  "4-6": "commercants.form.pv46",
  "7-10": "commercants.form.pv710",
  "11+": "pricing.pvTier.11plus",
};

export function getPvTierLabel(pv: PvTier, lang: Rv2Lang = "fr"): string {
  return rv2T(lang, PV_TIER_KEYS[pv]);
}

/** @deprecated Use getPvTierLabel(pv, lang) */
export const PV_TIER_LABELS: Record<PvTier, string> = {
  "1": "1 point de vente",
  "2-3": "2 à 3 points de vente",
  "4-6": "4 à 6 points de vente",
  "7-10": "7 à 10 points de vente",
  "11+": "Sur devis (11+ points de vente)",
};

export function getBillingDisplay(
  pv: PvTier,
  period: BillPeriod,
  lang: Rv2Lang = "fr",
  biz: BizType = "physique",
): BillingDisplay {
  const resolved = resolveLandingCatalogPrices(pv, biz);
  const mensuel = resolved.mensuel;
  const annuel = resolved.annuel;
  const isQuote = annuel === null;

  if (isQuote) {
    return {
      price: null,
      priceLabel: rv2T(lang, "pricing.quote"),
      unit: "",
      note: "",
      save: "",
      hasSave: false,
      isQuote: true,
    };
  }

  const savings = Math.round((mensuel! * 12 - annuel!) / 100) * 100;
  const annualMonthlyEquiv = Math.round(annuel! / 12);

  const byPeriod: Record<BillPeriod, Omit<BillingDisplay, "isQuote">> = {
    mensuel: {
      price: mensuel,
      priceLabel: formatMad(mensuel!),
      unit: rv2T(lang, "pricing.unit.mensuel"),
      note: rv2T(lang, "pricing.note.mensuel"),
      save: "",
      hasSave: false,
    },
    annuel: {
      price: annuel,
      priceLabel: formatMad(annuel!),
      unit: rv2T(lang, "pricing.unit.annuel"),
      note: rv2T(lang, "pricing.note.annuel", { amount: formatMad(annualMonthlyEquiv) }),
      save: rv2T(lang, "pricing.save.annuel", { amount: formatMad(savings) }),
      hasSave: true,
    },
  };

  return { ...byPeriod[period], isQuote: false };
}

export type CheckoutPlanInput = {
  code: string;
  label?: { fr?: string; en?: string };
  amountMad: number | null;
};

export type CheckoutPlanCardDisplay = {
  code: string;
  title: string;
  priceFmt: string;
  unit: string;
  subtext: string;
  benefit: string;
  benefitTone: "green" | "gray";
  showBestOffer: boolean;
};

export type CheckoutSummary = {
  pvLabel: string;
  planLabel: string;
  planAmountDisplay: string;
  dueTodayDisplay: string;
  isQuote: boolean;
};

export type CheckoutRecurrence = {
  recurAmountDisplay: string;
  recurEvery: string;
  recurNextDate: string;
};

function planTitle(plan: CheckoutPlanInput | undefined, code: string, lang: Rv2Lang) {
  const keyByCode: Record<string, string> = {
    yearly: "pricing.bill.annuel",
    monthly: "pricing.bill.mensuel",
  };
  const localized = rv2T(lang, keyByCode[code] || code);
  if (lang === "fr") {
    return plan?.label?.fr || localized;
  }
  if (lang === "en") {
    return plan?.label?.en || localized;
  }
  return localized;
}

export function buildCheckoutPlanCards(
  plans: CheckoutPlanInput[],
  lang: Rv2Lang = "fr",
): CheckoutPlanCardDisplay[] {
  const byCode = Object.fromEntries(plans.map((p) => [p.code, p]));
  const annual = byCode.yearly?.amountMad ?? null;
  const monthly = byCode.monthly?.amountMad ?? null;
  const noPrice = annual == null && monthly == null;

  const cards: CheckoutPlanCardDisplay[] = [];

  if (byCode.yearly) {
    cards.push({
      code: "yearly",
      title: planTitle(byCode.yearly, "yearly", lang),
      priceFmt: noPrice || annual == null ? "—" : formatMad(annual),
      unit: rv2T(lang, "checkout.plan.unit.perYear"),
      subtext:
        noPrice || annual == null
          ? ""
          : rv2T(lang, "checkout.plan.card.annualSub", {
              amount: formatMad(Math.round(annual / 12)),
            }),
      benefit: rv2T(lang, "checkout.plan.card.annualBenefit"),
      benefitTone: "green",
      showBestOffer: true,
    });
  }

  if (byCode.monthly) {
    cards.push({
      code: "monthly",
      title: planTitle(byCode.monthly, "monthly", lang),
      priceFmt: noPrice || monthly == null ? "—" : formatMad(monthly),
      unit: rv2T(lang, "checkout.plan.unit.perMonth"),
      subtext: rv2T(lang, "checkout.plan.card.monthlyFlex"),
      benefit: rv2T(lang, "pricing.trust.noCommitment"),
      benefitTone: "gray",
      showBestOffer: false,
    });
  }

  return cards;
}

export function getPlanLabelLower(planCode: string, lang: Rv2Lang = "fr"): string {
  const byCode: Record<string, string> = {
    yearly: rv2T(lang, "checkout.plan.label.yearly"),
    monthly: rv2T(lang, "checkout.plan.label.monthly"),
  };
  return byCode[planCode] || planCode;
}

export function buildCheckoutRecurrence(
  planCode: string,
  plan: CheckoutPlanInput | null,
  lang: Rv2Lang = "fr",
): CheckoutRecurrence {
  const isQuote = plan?.amountMad == null;
  const amount = plan?.amountMad ?? 0;
  const recurMap: Record<string, { everyKey: string; months: number }> = {
    monthly: { everyKey: "checkout.plan.recur.monthly", months: 1 },
    yearly: { everyKey: "checkout.plan.recur.yearly", months: 12 },
  };
  const rec = recurMap[planCode] || recurMap.yearly;
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + rec.months, now.getDate());
  const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";

  return {
    recurAmountDisplay: isQuote
      ? rv2T(lang, "pricing.quote")
      : `${formatMad(amount)} MAD`,
    recurEvery: rv2T(lang, rec.everyKey),
    recurNextDate: next.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
  };
}

export function buildCheckoutSummary(
  pv: PvTier,
  plan: CheckoutPlanInput | null,
  lang: Rv2Lang = "fr",
): CheckoutSummary {
  const isQuote = pv === "11+" || plan?.amountMad == null;
  const amount = plan?.amountMad ?? 0;
  const planLabelText = plan ? planTitle(plan, plan.code, lang) : "—";
  const quote = rv2T(lang, "pricing.quote");

  return {
    pvLabel: getPvTierLabel(pv, lang),
    planLabel: planLabelText,
    planAmountDisplay: isQuote ? quote : `${formatMad(amount)} MAD`,
    dueTodayDisplay: isQuote ? quote : `${formatMad(amount)} MAD`,
    isQuote,
  };
}

export {
  ALL_CITIES,
  CITY_OTHER,
  ONSITE_CITIES,
  filterCities,
  isCityOther,
  isOnsiteCity,
  isValidDemoCity,
  normalizeCitySearch,
  formatCityRecap,
  type DemoCity,
} from "./cities";

export type PvSelection = PvTier | "marque-blanche";

const PV_SELECTION_VALUES: PvSelection[] = ["1", "2-3", "4-6", "7-10", "11+", "marque-blanche"];

export function parsePvSelectionFromQuery(raw: string | null): PvSelection {
  if (raw && PV_SELECTION_VALUES.includes(raw as PvSelection)) {
    return raw as PvSelection;
  }
  return "1";
}

export function parseBillPeriodFromQuery(raw: string | null): BillPeriod {
  if (raw && BILL_PERIOD_VALUES.includes(raw as BillPeriod)) {
    return raw as BillPeriod;
  }
  return "mensuel";
}

export function commercantsOfferHref(
  pv: PvTier | "marque-blanche",
  lang?: string,
  biz?: BizType,
  plan?: BillPeriod,
): string {
  const params = new URLSearchParams({ pv });
  if (biz) params.set("biz", biz);
  if (plan && BILL_PERIOD_VALUES.includes(plan)) params.set("plan", plan);
  const href = `/rewards_v2/commercants?${params.toString()}#demo`;
  return lang ? withLang(href, lang) : href;
}
